import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ShieldCheck, ArrowRight, PlayCircle, FileBarChart2, Home, Utensils, Users,
  Check, Lock, ClipboardCheck, Clock, BadgeCheck, Star
} from "lucide-react";

const BRAND_LOGO = "https://customer-assets.emergentagent.com/job_care-center-taxes/artifacts/l28sqvje_FullLogo_Transparent_NoBuffer.png";

const TIERS = [
  { id: "basic", label: "Basic", price: 99, pages: 100, tagline: "Perfect for smaller centers", accent: "indigo" },
  { id: "premium", label: "Premium", price: 149, pages: 350, tagline: "Best for growing centers", accent: "purple", popular: true },
  { id: "premium_plus", label: "Premium Plus", price: 249, pages: 1000, tagline: "For high-volume centers", accent: "emerald" },
];

const FEATURES = [
  { icon: FileBarChart2, color: "bg-indigo-100 text-indigo-600", title: "CACFP Reports", desc: "Generate complete CACFP reports with confidence." },
  { icon: Home, color: "bg-blue-100 text-blue-600", title: "Use Charges", desc: "Automatically calculate and allocate use-of-space expenses with ease." },
  { icon: Utensils, color: "bg-orange-100 text-orange-600", title: "Food Charges", desc: "Track and organize all food-related CACFP expenses in one place." },
  { icon: Users, color: "bg-emerald-100 text-emerald-600", title: "Staff Charges", desc: "Allocate staffing and labor costs accurately and save hours of work." },
];

const TRUST_BADGES = [
  { icon: Lock, color: "text-emerald-600", title: "Secure & Private", desc: "Bank-level encryption keeps your data safe." },
  { icon: BadgeCheck, color: "text-blue-600", title: "Accountant Friendly", desc: "Clean, organized reports your accountant will love." },
  { icon: ClipboardCheck, color: "text-purple-600", title: "Audit Ready", desc: "Stay compliant with confidence." },
  { icon: Clock, color: "text-indigo-600", title: "Save Time", desc: "What used to take hours now takes minutes." },
];

const EXTRACTED = ["Bank Statements", "CACFP Reports", "Subsidy / 1099s", "Utility Bills", "Receipts", "Payroll Reports"];

const accentClasses = {
  indigo:  { border: "border-slate-200", price: "text-slate-900",   title: "text-indigo-600" },
  purple:  { border: "border-purple-500 ring-4 ring-purple-100",  price: "text-slate-900", title: "text-purple-600" },
  emerald: { border: "border-slate-200", price: "text-slate-900",   title: "text-emerald-600" },
};

function DashboardPreview() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden">
      {/* Mock header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <img src={BRAND_LOGO} alt="" className="w-6 h-6 object-contain" />
          <span className="text-xs font-bold text-slate-700">Today's Kidz Tax</span>
        </div>
        <span className="text-xs text-slate-400">Center Name ▾</span>
      </div>
      <div className="flex">
        {/* Mini sidebar */}
        <div className="w-32 border-r border-slate-100 py-3 px-3 bg-slate-50/50">
          {["Dashboard", "Documents", "CACFP Overview", "Use Charges", "Food Charges", "Staff Charges", "Income", "Expenses", "Settings"].map((s, i) => (
            <p key={s} className={`text-[10px] py-1 px-1.5 rounded ${i === 0 ? "bg-indigo-50 text-indigo-700 font-semibold" : "text-slate-500"}`}>{s}</p>
          ))}
        </div>
        {/* Mini main */}
        <div className="flex-1 p-3 space-y-3">
          <p className="text-xs font-bold text-slate-900">Dashboard</p>
          <div className="grid grid-cols-5 gap-1.5">
            {[
              { label: "CACFP Reports", val: "4", color: "text-indigo-600" },
              { label: "Use Charges", val: "$12,450.75", color: "text-blue-600" },
              { label: "Food Charges", val: "$8,230.40", color: "text-orange-600" },
              { label: "Staff Charges", val: "$15,890.60", color: "text-emerald-600" },
              { label: "Total Expenses", val: "$36,571.75", color: "text-slate-800" },
            ].map(k => (
              <div key={k.label} className="border border-slate-100 rounded-md p-1.5">
                <p className="text-[7px] uppercase tracking-wider text-slate-400">{k.label}</p>
                <p className={`text-[9px] font-bold mt-0.5 ${k.color}`}>{k.val}</p>
                <p className="text-[6px] text-slate-400 mt-0.5">YTD Total</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="border border-slate-100 rounded-md p-2">
              <p className="text-[9px] font-semibold text-slate-700 mb-1">Allocation Summary</p>
              <div className="flex items-center gap-2">
                <div className="w-12 h-12 rounded-full"
                  style={{ background: "conic-gradient(#6366F1 0% 34%, #F97316 34% 57%, #10B981 57% 100%)" }} />
                <div className="space-y-0.5 text-[7px] text-slate-500">
                  <p><span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500 mr-1" />Use 34%</p>
                  <p><span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500 mr-1" />Food 23%</p>
                  <p><span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1" />Staff 43%</p>
                </div>
              </div>
            </div>
            <div className="border border-slate-100 rounded-md p-2">
              <p className="text-[9px] font-semibold text-slate-700 mb-1">Recent Documents</p>
              {["Bank Statement - Apr 2024.pdf", "CACFP Report - Apr 2024.pdf", "Payroll Report - Apr 2024.pdf"].map(d => (
                <p key={d} className="text-[7px] text-slate-500 truncate">{d}</p>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {["Upload Documents", "Generate Report", "View Transactions", "Export to PDF"].map(q => (
              <div key={q} className="border border-slate-100 rounded p-1 text-center">
                <p className="text-[7px] font-semibold text-slate-700">{q}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Auto-redirect signed-in visitors straight to the Dashboard so they never
  // see the public marketing page on todayskidztax.com after logging in.
  // Replaces the bare "You're already signed in" interstitial — same behavior,
  // cleaner UX.
  useEffect(() => {
    if (user) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);

  if (user) return null;  // brief blank while the redirect lands

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "IBM Plex Sans, sans-serif" }}>
      {/* Nav */}
      <header className="border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-testid="landing-header">
          <Link to="/" className="flex items-center gap-3">
            <img src={BRAND_LOGO} alt="Today's Kidz Simple Sign-In" className="h-12 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8 text-sm font-medium text-slate-700">
            <a href="#features" className="hover:text-purple-600 transition-colors" data-testid="nav-features">Features</a>
            <a href="#pricing" className="hover:text-purple-600 transition-colors" data-testid="nav-pricing">Pricing</a>
            <a href="#how-it-works" className="hover:text-purple-600 transition-colors" data-testid="nav-how">How It Works</a>
            <a href="#resources" className="hover:text-purple-600 transition-colors" data-testid="nav-resources">Resources</a>
            <a href="#about" className="hover:text-purple-600 transition-colors" data-testid="nav-about">About</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-slate-700 hover:text-purple-600 text-sm font-semibold transition-colors" data-testid="header-login-link">
              Sign In
            </Link>
            <button
              onClick={() => navigate("/register")}
              data-testid="header-get-started-btn"
              className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-all shadow-sm"
            >
              Start 14-Day Free Trial
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-12 pb-16 lg:pt-20 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-purple-50 border border-purple-200 text-purple-700 text-xs font-semibold px-3 py-1.5 rounded-full mb-6">
              <ShieldCheck className="w-3.5 h-3.5" />
              14-Day Free Trial — No Credit Card Required
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-[1.05] tracking-tight" style={{ fontFamily: "Manrope" }}>
              Childcare Center<br />Tax Reporting<br />
              <span style={{ background: "linear-gradient(135deg, #7c3aed, #4f46e5)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Made Simple
              </span>
            </h1>
            <p className="text-base lg:text-lg text-slate-600 mt-6 max-w-xl leading-relaxed">
              Upload once. We automatically organize your documents and generate CACFP reports by{" "}
              <span className="text-blue-600 font-semibold">Use Charges</span>,{" "}
              <span className="text-orange-600 font-semibold">Food Charges</span>, and{" "}
              <span className="text-emerald-600 font-semibold">Staff Charges</span>,{" "}
              and tax-ready reports in minutes.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-8">
              <button
                onClick={() => navigate("/register")}
                data-testid="hero-start-trial-btn"
                className="flex items-center gap-2 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold px-7 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl text-sm"
              >
                Start Your 14-Day Free Trial <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#how-it-works"
                data-testid="hero-how-btn"
                className="flex items-center gap-2 border-2 border-slate-200 bg-white text-slate-700 hover:bg-slate-50 font-semibold px-7 py-3.5 rounded-xl transition-all text-sm"
              >
                <PlayCircle className="w-4 h-4" /> See How It Works
              </a>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8 text-xs text-slate-600">
              {["No Credit Card Required", "Cancel Anytime", "Full Feature Access", "Designed for Childcare Centers"].map(t => (
                <div key={t} className="flex items-start gap-1.5">
                  <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>{t}</span>
                </div>
              ))}
            </div>
          </div>
          {/* Dashboard preview */}
          <div className="hidden lg:block">
            <DashboardPreview />
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-slate-50 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 text-center mb-12" style={{ fontFamily: "Manrope" }}>
            Everything You Need for CACFP &amp; Tax Season
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="features-grid">
            {FEATURES.map(({ icon: Icon, color, title, desc }) => (
              <div key={title} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md transition-all">
                <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-bold text-slate-900 text-lg mb-2" style={{ fontFamily: "Manrope" }}>{title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents We Extract */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 text-center mb-10" style={{ fontFamily: "Manrope" }}>
            Documents We Extract &amp; Organize
          </h2>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto" data-testid="extract-chips">
            {EXTRACTED.map(item => (
              <div key={item} className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-full px-5 py-2.5">
                <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <span className="text-slate-700 text-sm font-semibold">{item}</span>
              </div>
            ))}
          </div>
          <p className="text-slate-400 text-sm text-center mt-4">…and more</p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {TRUST_BADGES.map(({ icon: Icon, color, title, desc }) => (
              <div key={title} className="flex items-start gap-3 bg-slate-50/70 border border-slate-200 rounded-2xl p-5">
                <Icon className={`w-7 h-7 ${color} flex-shrink-0 mt-0.5`} />
                <div>
                  <p className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>{title}</p>
                  <p className="text-slate-600 text-xs mt-1 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-slate-50 py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl lg:text-4xl font-extrabold text-slate-900 text-center" style={{ fontFamily: "Manrope" }}>
            Simple, Transparent Pricing
          </h2>
          <p className="text-slate-600 text-center mt-3">Choose the plan that fits your center.</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12" data-testid="landing-pricing-cards">
            {TIERS.map((tier) => {
              const a = accentClasses[tier.accent];
              return (
                <div
                  key={tier.id}
                  data-testid={`landing-tier-${tier.id}`}
                  className={`relative bg-white border-2 ${a.border} rounded-2xl p-7 ${tier.popular ? "lg:scale-105 shadow-xl" : "shadow-sm"} transition-transform`}
                >
                  {tier.popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-[11px] font-bold uppercase tracking-wider px-4 py-1.5 rounded-full flex items-center gap-1.5">
                      <Star className="w-3 h-3 fill-current" /> Most Popular
                    </div>
                  )}
                  <div>
                    <h3 className={`text-2xl font-extrabold ${a.title}`} style={{ fontFamily: "Manrope" }}>{tier.label}</h3>
                    <p className="text-sm text-slate-500 mt-1">{tier.pages.toLocaleString()} pages included</p>
                  </div>
                  <div className="mt-5">
                    <span className="text-5xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>${tier.price}</span>
                    <span className="text-base text-slate-400 font-normal">/year</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-2">{tier.tagline}</p>
                  <p className="flex items-center gap-1.5 mt-4 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500" />All core features included</p>
                  <button
                    onClick={() => navigate("/register")}
                    data-testid={`landing-tier-${tier.id}-cta`}
                    className={`w-full mt-6 py-3 rounded-xl text-sm font-bold transition-all ${
                      tier.popular ? "bg-purple-600 hover:bg-purple-700 text-white" : "border-2 border-slate-200 hover:border-slate-300 text-slate-700"
                    }`}
                  >
                    Start Free Trial
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-slate-400 text-xs text-center mt-6">
            14-Day Free Trial · No Credit Card Required · Cancel Anytime
          </p>
        </div>
      </section>

      {/* CTA */}
      <section id="how-it-works" className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="rounded-2xl p-10 lg:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6" style={{ background: "linear-gradient(135deg, #4f46e5, #2563eb)" }}>
            <div>
              <h2 className="text-2xl lg:text-3xl font-extrabold text-white" style={{ fontFamily: "Manrope" }}>
                Stop Spending Hours on CACFP &amp; Tax Reports
              </h2>
              <p className="text-white/80 mt-2 text-sm lg:text-base">Start your 14-day free trial today. No credit card required.</p>
            </div>
            <button
              onClick={() => navigate("/register")}
              data-testid="cta-start-trial-btn"
              className="flex items-center gap-2 bg-white text-blue-700 font-bold px-7 py-3.5 rounded-xl hover:bg-slate-50 transition-all text-sm shadow-lg whitespace-nowrap"
            >
              Start Free Trial — 14 Days Free <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-100 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-slate-500 text-xs">
            <img src={BRAND_LOGO} alt="" className="h-7 w-auto opacity-80" />
            <span>© 2026 Today's Kidz Simple Sign-In. Tax software for childcare centers.</span>
          </div>
          <div className="flex items-center gap-5 text-xs text-slate-500">
            <Link to="/login" className="hover:text-slate-900">Sign In</Link>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
            <a href="#about" className="hover:text-slate-900">About</a>
            <Link to="/terms" className="hover:text-slate-900" data-testid="footer-terms-link">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-900" data-testid="footer-privacy-link">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
